import { getTicks } from '@visx/scale';
import { useMemo } from 'react';

import borders from '@dynatrace/strato-design-tokens/borders';
import colors from '@dynatrace/strato-design-tokens/colors';

import { ganttChartGridCSS } from './DataTableV2GanttChartGrid.sty.js';
import { useScaleAndFormatter } from './hooks/useScaleAndFormatter.js';
import { getTickValueAsNumber } from '../../../../charts/core/utils/get-tick-value-as-number.js';
import {
  getGanttHorizontalTimeAxisNumTicks,
  getHorizontalNumTicks,
} from '../../../../charts/core/utils/ticks.js';
import type { DataTableV2GanttColumnConfig } from '../ColumnTypes/ColumnTypes.js';

export const DataTableV2GanttChartGrid = ({
  width,
  height,
  min,
  max,
  config,
}: {
  width: number;
  height: number;
  min: number;
  max: number;
  config: DataTableV2GanttColumnConfig;
}) => {
  const { xAxisType = 'numerical' } = config;
  const { scale } = useScaleAndFormatter(width, config, min, max);
  const numTicks =
    xAxisType === 'time'
      ? getGanttHorizontalTimeAxisNumTicks(width)
      : getHorizontalNumTicks(width);
  const ticks = getTicks(scale, numTicks);

  // if the first tick is the same as min remove it, as the gantt column already draws a line at the start
  if (getTickValueAsNumber(ticks.at(0)?.valueOf()) === min) {
    ticks.shift();
  }

  // if the last tick is the same as max remove it, as the gantt column already draws a line at the end
  if (getTickValueAsNumber(ticks.at(-1)?.valueOf()) === max) {
    ticks.pop();
  }

  const lines = useMemo(
    () =>
      ticks.map((rawTick, idx) => {
        const tick = Math.max(scale(rawTick), 0.5);
        return (
          <line
            key={+rawTick}
            x1={tick}
            x2={tick}
            y1={0}
            y2={height}
            strokeWidth={borders.Width.Default}
            stroke={colors.Border.Neutral.Default}
          />
        );
      }),
    [height, scale, ticks],
  );

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      height={height}
      width={width}
      className={ganttChartGridCSS}
    >
      {lines}
    </svg>
  );
};
